import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import Body from '@aytm/app/stats/page/question-views/cjc/components/Body'
import Head from '@aytm/app/stats/page/question-views/cjd/components/head/Head'
import HeadIcon from '@aytm/app/stats/page/question-views/cjc/components/head/HeadIcon'
import DataRun from '@aytm/app/stats/page/question-views/components/data-run/DataRun'
import useConjointState from '@aytm/app/stats/page/question-views/cjc/states/useConjointState'
import useConjointExport from '@aytm/app/stats/page/question-views/cjc/states/useConjointExport'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

const RUN_ICON = (
  <>
    <HeadIcon />
    <span>
      Conjoint
      <br />
      Segmentation<sup>SM</sup>
    </span>
  </>
)

// Everything the researcher sees once the clusters are solved: the question's own head, carrying the
// persona selector, and the shared body that draws them. The export handlers only this half needs
// are resolved here, so a question with nothing to draw never pays for them.
const BlockBody = ({ state, ...props }) => {
  const {
    answer,
    batches,
    chartLegendMode,
    colorTheme,
    components,
    designSheet,
    features,
    fullViewOnly,
    globalChartMode,
    globalZoomLevel,
    hideTotal,
    isAnimated,
    link,
    position,
    processText,
    questionChartMode,
    questionId,
    questionKey,
    questions,
    questionType,
    showPersonaCard,
    traits,
    visualPosition,
    onChartExport,
    onChartReady,
    onDesignExport,
    onExport,
    onMarketSimulatorExport,
    onScrollToQuestion
  } = props

  // Signed in, the feature on, and numbers to export: all three the body already holds.
  const showExport = 'loading' !== answer && Boolean(PageGlobals.USER_ID) && !features?.export?.disabled

  const { onHeadExport, onSimulatorExport } = useConjointExport({
    state,
    answer,
    batches,
    designSheet,
    link,
    position,
    questionId,
    questionKey,
    questionType,
    visualPosition,
    onExport,
    onDesignExport,
    onMarketSimulatorExport
  })

  return (
    <>
      <Head
        answer={answer}
        isLite={state.isLiteView}
        isSummaryChartView={state.isSummaryChartView}
        isAwaitingFullSlice={state.isAwaitingFullSlice}
        showExport={showExport}
        fullViewOnly={fullViewOnly}
        onExport={onHeadExport}
        onShowBar={state.onShowBar}
        onShowVisual={state.onShowVisual}
        traits={traits}
        questions={questions}
        persona={state.persona}
        showPersonaCard={showPersonaCard}
        onSelectPersona={state.onSelectPersona}
        onScrollToQuestion={onScrollToQuestion}
      />
      <Body
        {...state}
        answer={answer}
        batches={batches}
        questionType={questionType}
        questionId={questionId}
        questionKey={questionKey}
        components={components}
        features={features}
        colorTheme={colorTheme}
        chartLegendMode={chartLegendMode}
        questionChartMode={questionChartMode}
        globalChartMode={globalChartMode}
        globalZoomLevel={globalZoomLevel}
        hideTotal={hideTotal}
        isAnimated={isAnimated}
        processText={processText}
        onChartExport={onChartExport}
        onChartReady={onChartReady}
        onMarketSimulatorExport={onSimulatorExport}
      />
    </>
  )
}

BlockBody.displayName = 'ConjointSegmentationBlockBody'

// Conjoint Segmentation: the run panel, its own head - which carries the cluster persona selector
// - and the shared body. The clusters are the point of the question, so its chart always draws.
const Block = props => {
  const {
    answer,
    batches,
    calcBadData,
    defaultChartMode,
    defaultSortOrder,
    fullViewOnly,
    globalChartMode,
    globalZoomLevel,
    params,
    paramsRevision,
    questionChartMode,
    questionKey,
    questionType,
    schemaKey,
    onBadData,
    onReady,
    onStateChange
  } = props

  // the question's own rule, applied to the payload on screen -- read here rather than taken as a
  // flag, so what the block draws can never disagree with what it was handed
  const badData = Boolean(calcBadData?.(answer))

  // the api the block hands up is owed on mount, so the state lives here rather than in the body
  const state = useConjointState({
    answer,
    batches,
    questionType,
    badData,
    defaultSortOrder,
    defaultChartMode,
    questionChartMode,
    globalChartMode,
    globalZoomLevel,
    fullViewOnly,
    params,
    paramsRevision,
    onReady,
    onStateChange
  })

  return (
    <BlockWrapper answer={answer}>
      <DataRun
        answer={answer}
        badData={badData}
        type={questionType}
        jobType="conjoint"
        questionKey={questionKey}
        schemaKey={schemaKey}
        icon={RUN_ICON}
        colorTheme={props.colorTheme}
        calcBadData={calcBadData}
        onBadData={onBadData}
      />
      {/* while the numbers have not been crunched there is nothing else to show */}
      {badData ? null : <BlockBody {...props} state={state} />}
    </BlockWrapper>
  )
}

Block.displayName = 'ConjointSegmentationBlock'

Block.propTypes = {
  ...Body.propTypes,
  answer: answerPropType,
  calcBadData: PropTypes.func,
  defaultChartMode: PropTypes.string,
  defaultSortOrder: PropTypes.string,
  params: PropTypes.object,
  // bumped by the shell every time it re-reads the params off the model
  paramsRevision: PropTypes.number,
  schemaKey: PropTypes.string,
  fullViewOnly: PropTypes.bool,
  traits: PropTypes.array,
  questions: PropTypes.array,
  showPersonaCard: PropTypes.bool,
  designSheet: PropTypes.object,
  link: PropTypes.string,
  position: PropTypes.number,
  visualPosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBadData: PropTypes.func,
  onDesignExport: PropTypes.func,
  onExport: PropTypes.func,
  onReady: PropTypes.func,
  onScrollToQuestion: PropTypes.func,
  onStateChange: PropTypes.func
}

// the body is handed the block's own props, plus the state the block owns
BlockBody.propTypes = { ...Block.propTypes, state: PropTypes.object }

export default Block
